import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Spinner, Alert, Card, Container, Row, Col } from 'react-bootstrap';
import { FaPencilAlt, FaTrash } from 'react-icons/fa'; // Importing icons
import api from '../../services/api';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import TopBar from '../../components/TopBar';
import moment from 'moment';


const HolidayManagement = () => {
  const [holidays, setHolidays] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentHoliday, setCurrentHoliday] = useState({ description: '', date: '', type: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [holidayCounts, setHolidayCounts] = useState({ national: 0, floating: 0 });

  useEffect(() => {
    fetchHolidayCounts();
    fetchHolidays();
  }, []);

  const fetchHolidayCounts = async () => {
    try {
      const response = await api.get('/holiday-counts');
      setHolidayCounts(response.data.holiday_counts);
    } catch (error) {
      console.error('Error fetching holiday counts:', error);
    }
  };


  const fetchHolidays = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/holidays');
      setHolidays(response.data);
    } catch (err) {
      setError('Failed to fetch holidays');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!currentHoliday.type) {
        setError('Please select holiday type');
        setLoading(false);
        return;
      }
      if (currentHoliday.id) {
        await api.put(`/holidays/${currentHoliday.id}`, currentHoliday);
      } else {
        await api.post('/holidays', currentHoliday);
      }
      fetchHolidayCounts();
      fetchHolidays();
      setShowModal(false);
    } catch (err) {
      setError('Failed to save holiday');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/holidays/${id}`);
      fetchHolidays();
    } catch (err) {
      setError('Failed to delete holiday');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (holiday = { description: '', date: '', type: '' }) => {
    setCurrentHoliday({
      ...holiday,
      date: holiday.date ? moment(holiday.date).format('YYYY-MM-DD') : ''
    });
    setShowModal(true);
  };

  return (
    <Layout>
    <TopBar title="Holiday Management" />
    <Container>
      <Row className="mt-4">
        <Col md={6}>
          <Card className="mb-4" style={{ backgroundColor: '#f6c23e' }}>
            <Card.Body>
              <Card.Title>National Holidays</Card.Title>
              <Card.Text>{holidayCounts.national}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="mb-4" style={{ backgroundColor: '#36b9cc' }}>
            <Card.Body>
              <Card.Title>Floating Holidays</Card.Title>
              <Card.Text>{holidayCounts.floating}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
    <Container className="mt-4" fluid>
      {error && <Alert variant="danger">{error}</Alert>}
      <Row className="mb-3">
        <Col className="d-flex justify-content-end">
          <Button variant="primary" onClick={() => openModal()}>
            + Add Holiday
          </Button>
        </Col>
      </Row>
  
      <Card className="shadow-sm">
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : holidays.length === 0 ? (
            <div className="text-center text-muted py-4">
              No holidays found. Click "Add Holiday" to get started.
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead className="table-light">
                  <tr>
                    <th>Description</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {holidays.map((holiday) => (
                    <tr key={holiday.id}>
                      <td>{holiday.description}</td>
                      <td>{moment(holiday.date).format('DD-MM-YYYY')}</td>
                      <td className="text-capitalize">{holiday.type==="festival"?"Floating":holiday.type}</td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-3">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openModal(holiday)}
                          >
                            <FaPencilAlt />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(holiday.id)}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  
    <Modal show={showModal} onHide={() => setShowModal(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">
          {currentHoliday.id ? 'Edit Holiday' : 'Add Holiday'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Description</Form.Label>
            <Form.Control
              type="text"
              value={currentHoliday.description}
              onChange={(e) =>
                setCurrentHoliday({ ...currentHoliday, description: e.target.value })
              }
              placeholder="Enter holiday description"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Date</Form.Label>
            <Form.Control
              type="date"
              value={currentHoliday.date}
              onChange={(e) =>
                setCurrentHoliday({ ...currentHoliday, date: e.target.value })
              }
              className="form-control focus-visible"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Type</Form.Label>
            <Form.Select
              value={currentHoliday.type}
              onChange={(e) =>
                setCurrentHoliday({ ...currentHoliday, type: e.target.value })
              }
            >
              <option value="" disabled>Please select</option>
              <option value="national">National</option>
              <option value="floating">Floating</option>
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowModal(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  </Layout>
  
  );
};

export default HolidayManagement;
